package diana.soleil.design.iraniancanadienbazzar.core.common

/**
 * Outcome of a data-layer operation. Repositories return this instead of throwing, so callers
 * (ViewModels) can render [Loading]/empty/error states without try-catch sprinkled through the UI.
 *
 * Keep [AppError] coarse and UI-mappable — feature code maps an error to a localized message; it
 * never embeds copy here (golden rule: no hardcoded user-facing text in shared logic).
 */
sealed interface DataResult<out T> {
    data class Success<T>(val data: T) : DataResult<T>
    data class Failure(val error: AppError) : DataResult<Nothing>
}

/** Coarse, presentation-mappable failure categories. The optional [cause] aids logging only. */
sealed class AppError(val cause: Throwable? = null) {
    /** No connectivity / request could not reach the backend. */
    class Network(cause: Throwable? = null) : AppError(cause)

    /** Caller is not allowed (failed security rules / not signed in where required). */
    class Unauthorized(cause: Throwable? = null) : AppError(cause)

    /** The requested document/resource does not exist. */
    class NotFound(cause: Throwable? = null) : AppError(cause)

    /** Auth: wrong/disabled credentials, bad email, or a too-weak password (sign-in/up rejected). */
    class InvalidCredentials(cause: Throwable? = null) : AppError(cause)

    /** Auth: the target already exists (e.g. email already registered on sign-up). */
    class Conflict(cause: Throwable? = null) : AppError(cause)

    /** Anything else — decoding failures, unexpected backend errors. */
    class Unknown(cause: Throwable? = null) : AppError(cause)
}

inline fun <T, R> DataResult<T>.map(transform: (T) -> R): DataResult<R> = when (this) {
    is DataResult.Success -> DataResult.Success(transform(data))
    is DataResult.Failure -> this
}

inline fun <T> DataResult<T>.onSuccess(action: (T) -> Unit): DataResult<T> {
    if (this is DataResult.Success) action(data)
    return this
}

inline fun <T> DataResult<T>.onFailure(action: (AppError) -> Unit): DataResult<T> {
    if (this is DataResult.Failure) action(error)
    return this
}

fun <T> DataResult<T>.getOrNull(): T? = (this as? DataResult.Success)?.data
